import { ButtonGroup, Intent } from "@blueprintjs/core";
import {
  BaseFilterCriteriaInfo,
  BaseSelectItem,
  FieldSet,
  IDataTable2Props,
  IDataTableColumn,
  IDataTableProps,
  IFieldSetProps,
  InlineSearchCriteriaPanel,
  ISearchCriteriaValues,
  IValue,
  SearchPanel,
  SortType,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";
import { useLocation } from "react-router";

import { AddButton, CustomDataTable, ExportButton, ICustomBreadcrumbProps, PageBase } from "../";
import { EPlatform, FileResponse } from "../../api";
import { useEventsContext, useGlobalData, useSearchCriteriaContext } from "../../contexts";
import { useTl } from "../../hooks";
import { ETLCodes } from "../../locales";

interface ISearchTablePageProps<TResults, TSearch>
  extends Pick<IDataTable2Props<any>, "customizeRowStyle">,
    Pick<IFieldSetProps, "rightElement" | "buttons"> {
  searchFunction: (sObj: ISearchCriteriaValues) => TResults | Promise<TResults>;
  getCriteriasFunction?: () => Promise<BaseFilterCriteriaInfo[]>;
  exportFunction?: (sObj: ISearchCriteriaValues) => Promise<FileResponse>;
  addFunc?: () => void;
  columns: Array<IDataTableColumn>;
  breadCrumbs?: ICustomBreadcrumbProps[];
  criteriasTlPrefix?: string;
  defaultCriterias?: IValue[];
  withCard?: boolean;
  dateFormat?: string;
  platform?: EPlatform;
  filterPlaceholder?: string;
  enableFilter?: boolean;
  saveCriterias?: boolean;
  initialSearch?: boolean;
  overrideListValues?: {
    [key: string]: (e: BaseSelectItem) => string;
  };
  sortKeys?: {
    [key: string]: SortType;
  };
  title?: string;
  overrideTableStateSearch?: (sObj: TSearch) => any;
  showAllCriteriaRemovalButton?: boolean;
  allCriteriaRemovalMode?: "RemoveAll" | "ReturnDefault";
  triggerSearchOnAllCriteriaRemoved?: boolean;
  imageSource?: string;
  filterMode?: "OnBlurOrEnter" | "OnChange" | "OnEnter";
  criteriaSavePrefix?: string;
}

export function SearchTablePage<TResults, TSearch>({
  searchFunction,
  exportFunction,
  getCriteriasFunction,
  initialSearch = true,
  columns,
  saveCriterias = true,
  breadCrumbs,
  withCard = true,
  criteriasTlPrefix,
  overrideListValues,
  sortKeys = {},
  platform = EPlatform.CARE4DOGS,
  defaultCriterias = [],
  overrideTableStateSearch,
  rightElement,
  buttons,
  addFunc,
  customizeRowStyle,
  title,
  filterPlaceholder,
  enableFilter = true,
  showAllCriteriaRemovalButton = true,
  allCriteriaRemovalMode = "RemoveAll",
  triggerSearchOnAllCriteriaRemoved = false,
  imageSource = null,
  filterMode = "OnChange",

  criteriaSavePrefix = "",
}: ISearchTablePageProps<TResults, TSearch>) {
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: enableFilter,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: sortKeys,
  });
  const { currentLanguage } = useGlobalData();
  let { totalCount } = tableState;
  const [criteriasLoading, setCriteriasLoading] = React.useState(false);

  const [currentsObj, setcurrentsObj] = React.useState<ISearchCriteriaValues>();
  const location = useLocation();
  const currentSObj = React.useRef<ISearchCriteriaValues>();
  const { t, tUnsafe } = useTl();

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: getCriteriasFunction === undefined,
    trimSearchObjectStringValues: true,
  });

  //uwc-debug
  const OnRefreshSearchTablesEvent = React.useCallback(() => {
    search(currentSObj.current);
  }, [search]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
    return () => unsubscribeEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
  }, [OnRefreshSearchTablesEvent, subscribeToEvent, unsubscribeEvent]);

  const finalSearchCriteriaFunction = React.useMemo(() => {
    if (getCriteriasFunction || currentLanguage) {
      return () => getCriteriasFunction();
    }
  }, [currentLanguage, getCriteriasFunction]);

  const getCriterias = React.useCallback(async () => {
    if (!getCriteriasFunction) return null;
    setCriteriasLoading(true);
    const crit = await getCriteriasFunction();
    setCriteriasLoading(false);
    return crit;
  }, [getCriteriasFunction]);

  return (
    <PageBase withCard={withCard} breadCrumbs={breadCrumbs}>
      {imageSource && (
        <img
          src={imageSource}
          style={{
            height: "10rem",
            width: "20rem",
            display: "block",
            margin: "auto",
          }}
          alt="dysplasia"
        />
      )}
      {getCriteriasFunction && (
        <FieldSet title={title || t(ETLCodes.General_TableCriterias)}>
          <SearchPanel
            getCriteriasFunc={getCriterias}
            defaultCriterias={defaultCriterias}
            onSearch={(val: ISearchCriteriaValues) => {
              currentSObj.current = val;
              search(val);
            }}
            showRazButton={false}
            enableOrOnSameCriteria={false}
            translateFunc={tUnsafe}
            tlDataPrefix={criteriasTlPrefix}
            overrideListValues={overrideListValues}
            criteriaIntent="primary"
            storageMode={"sessionStorage"}
            storageKey={"search_" + location.pathname}
            dateFormat="dd-MM-yyyy"
            triggerInitialSearch={true}
            clearSelectQueryOnClose
          />
        </FieldSet>
      )}
      <FieldSet
        title={
          <>
            <ButtonGroup>
              {rightElement && rightElement}
              {addFunc && (
                <AddButton
                  onClick={(e) => {
                    e.stopPropagation();
                    addFunc();
                  }}
                  text={t(ETLCodes.General_Add)}
                  intent={Intent.PRIMARY}
                />
              )}
              {exportFunction && <ExportButton exportFunction={exportFunction} sObj={currentsObj} />}
            </ButtonGroup>
          </>
        }
        rightElement={
          <>
            {totalCount > 1
              ? t(ETLCodes.TableResults_Plural, {
                  count: totalCount.toLocaleString("be-BE"),
                })
              : t(ETLCodes.TableResults, {
                  count: totalCount.toLocaleString("be-BE"),
                })}
          </>
        }
        buttons={buttons}
      >
        <CustomDataTable
          filterPlaceholder={filterPlaceholder}
          tableState={tableState}
          loading={loading}
          columns={columns}
          customizeRowStyle={customizeRowStyle}
          searched={!!currentSObj.current}
          filterMode={filterMode}
        />
      </FieldSet>
    </PageBase>
  );
}
